import { useState } from 'react'
import { DefaultTheme } from 'styled-components'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Button from 'components/shared/Button'
import {
    Wrapper,
    HeaderWrapper,
    ZonesWrapper,
    Image,
    Flag,
    PriceWrapper,
} from './styles'
import LogoJamango from 'assets/images/logos/Jamango.svg'
import LogoRelax from 'assets/images/logos/Relax.svg'
import LogoSaunaria from 'assets/images/logos/Saunaria.svg'
import LogoJamangoDarkMode from 'assets/images/logos/Jamango-mode_dark.svg'
import LogoRelaxDarkMode from 'assets/images/logos/Relax-mode_dark.svg'
import LogoSaunariaDarkMode from 'assets/images/logos/Saunaria-mode_dark.svg'
import { FormattedMessage } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import { CardDetailDtoLimited, CardDtoWithBundle } from 'store/api-slice/types'
import UpgradeCardModal from 'routes/unlimited-card/CardConfiguration/UnlimitedCardConfigurator/UpgradeCardModal'
import {
    resetVoucherCard,
    setIsUnlimitedPlusBeenOffered,
    setVoucherCard,
} from 'store/vouchers-slice'
import Price from 'components/shared/Price'
import Features from 'components/shared/UnlimitedCard/components/Features'
import { setIsCalendarModalOpened } from 'store/unlimited-slice'

interface VoucherUnlimitedCardProps {
    index: number
    cardSpecifications: CardDtoWithBundle
    unlimitedPlusData: CardDtoWithBundle
    theme?: DefaultTheme
}

// TODO: unify component with the one for unlimited cards

const VoucherUnlimitedCard = ({
    index,
    cardSpecifications,
    unlimitedPlusData,
    theme,
}: VoucherUnlimitedCardProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const dispatch = useAppDispatch()
    const { calculateScroll } = useAutoScroll()

    const { hasUnlimitedPlusBeenOffered, vouchersBoxes } = useAppSelector(
        (state) => state.vouchers
    )

    const { detail, offers, product, zones, bundleData } = cardSpecifications
    const { productId, productType, unitPrice } = product || {}

    const isCurrentCardChosen = vouchersBoxes[index]?.productId === productId
    const isCurrentCardUnlimitedPlus =
        detail?.cardType?.value?.includes('unlimited_plus') || false
    const isScrollOff =
        document.documentElement.classList.contains('-modal-open')
    const hasDarkMode = theme?.mode === 'dark'

    const elementCalculateScroll = () =>
        calculateScroll({
            trigerringSection: 'vouchers_configuration',
            trigerringAction: 'choose_card',
            targetId: `voucher_preview-${index}`,
        })

    const handleChooseCardType = () => {
        if (!hasUnlimitedPlusBeenOffered && !isCurrentCardUnlimitedPlus) {
            dispatch(setIsUnlimitedPlusBeenOffered(true))
            !isScrollOff &&
                // TODO: check if avoiding dom manipulation is possible / useref etc
                document.documentElement.classList.add('-modal-open')
            return setIsModalOpen(true)
        }

        if (!productId || !detail || !bundleData) return

        dispatch(
            setVoucherCard({
                index,
                productId,
                cardDetail: { ...(detail as CardDetailDtoLimited) },
                bundleData: [...bundleData],
                unitPrice,
            })
        )
        elementCalculateScroll()
    }

    const handleCancelCardTypeChoice = () => {
        dispatch(resetVoucherCard(index))
        calculateScroll({
            trigerringSection: 'vouchers_configuration',
            trigerringAction: 'reject_card',
            targetId: `card_type-${index}`,
        })
    }

    const handleUpgradeCard = () => {
        const { product, detail, bundleData } = unlimitedPlusData

        if (!product || !detail?.cardType || !bundleData) return

        const { productId, productType } = product

        if (!productId || !productType) return

        dispatch(
            setVoucherCard({
                index,
                productId,
                cardDetail: { ...(detail as CardDetailDtoLimited) },
                bundleData: [...bundleData],
                unitPrice,
            })
        )
        document.documentElement.classList.remove('-modal-open')
        setIsModalOpen(false)
        elementCalculateScroll()
    }

    const handleChooseCurrentCardType = () => {
        if (!productId || !productType || !detail?.cardType || !bundleData)
            return

        dispatch(
            setVoucherCard({
                index,
                productId,
                cardDetail: { ...(detail as CardDetailDtoLimited) },
                bundleData: [...bundleData],
                unitPrice,
            })
        )
        document.documentElement.classList.remove('-modal-open')
        setIsModalOpen(false)
        elementCalculateScroll()
    }

    const setZoneLogo = (zone: string) => {
        interface Logotypes {
            [key: string]: {
                [key: string]: string
            }
        }

        const logotypes: Logotypes = {
            jamango: {
                light: LogoJamango,
                dark: LogoJamangoDarkMode,
            },
            relax: {
                light: LogoRelax,
                dark: LogoRelaxDarkMode,
            },
            saunaria: {
                light: LogoSaunaria,
                dark: LogoSaunariaDarkMode,
            },
        }

        return logotypes[zone].light
    }

    const openCalendarModal = () => {
        dispatch(setIsCalendarModalOpened(true))
    }

    return (
        <Wrapper
            isChosen={isCurrentCardChosen}
            align="stretch"
            pt={24}
            pb={[24, 32]}
            pl={[24, 32]}
            pr={[24, 32]}
            theme={theme}
        >
            <HeaderWrapper isPlus={isCurrentCardUnlimitedPlus}>
                {detail?.isRecommended && (
                    <Flag align="center">
                        <Text variant="bodyExtraSmall" color="grayscale_0">
                            <FormattedMessage
                                id="annual.card_recommended"
                                defaultMessage="Polecane"
                            />
                        </Text>
                    </Flag>
                )}
                <Text
                    variant="heading3"
                    color={hasDarkMode ? 'grayscale_200' : undefined}
                >
                    {detail?.name}
                </Text>
                <Text
                    variant="body"
                    color={hasDarkMode ? 'grayscale_500' : 'grayscale_700'}
                >
                    {detail?.description}
                </Text>

                <PriceWrapper direction="row">
                    {!!unitPrice && (
                        <Price
                            value={unitPrice}
                            size="heading1"
                            color={
                                hasDarkMode ? 'primary_500' : 'grayscale_900'
                            }
                        />
                    )}
                    <Text
                        variant="bodyLarge"
                        color={hasDarkMode ? 'grayscale_200' : 'grayscale_700'}
                        ml={6}
                    >
                        <FormattedMessage
                            id="annual.per_year_label"
                            defaultMessage="za rok"
                        />
                    </Text>
                </PriceWrapper>

                {isCurrentCardChosen && (
                    <Button
                        variant="borderPrimary"
                        hasArrow={false}
                        width="100%"
                        onClick={handleCancelCardTypeChoice}
                        testId={`voucher-${index}-button-decline-card${
                            isCurrentCardUnlimitedPlus ? '-plus' : ''
                        }`}
                    >
                        <FormattedMessage
                            id="annual.btn.cancel_card"
                            defaultMessage="Anuluj wybór"
                        />
                    </Button>
                )}
                {!isCurrentCardChosen && (
                    <Button
                        variant="primary"
                        hasArrow={false}
                        width="100%"
                        onClick={handleChooseCardType}
                        testId={`voucher-${index}-button-choose-card${
                            isCurrentCardUnlimitedPlus ? '-plus' : ''
                        }`}
                    >
                        <FormattedMessage
                            id="annual.btn.select_card"
                            defaultMessage="Wybierz kartę Unlimited"
                        />
                        {isCurrentCardUnlimitedPlus ? '+' : ''}
                    </Button>
                )}
            </HeaderWrapper>
            <Box
                direction="column"
                align="flex-start"
                pt={[24, 32]}
                pb={[18, 24]}
                gap={16}
            >
                <Features
                    offers={offers}
                    cardIndex={index}
                    isCurrentCardUnlimitedPlus={isCurrentCardUnlimitedPlus}
                    theme={theme}
                />
            </Box>
            <ZonesWrapper pt={[16, 24]} isFullWidth>
                <Text
                    variant="body"
                    mb={8}
                    color={hasDarkMode ? 'grayscale_200' : 'grayscale_900'}
                >
                    <FormattedMessage
                        id="annual.card_zones_label"
                        defaultMessage="Dostęp do stref:"
                    />
                </Text>
                <Box direction="row" gap={16}>
                    {zones?.map(
                        (zone, index) =>
                            zone.value && (
                                <Image
                                    key={`${zone.name}${index}`}
                                    src={setZoneLogo(zone.value)}
                                    alt=""
                                />
                            )
                    )}
                </Box>
            </ZonesWrapper>
            <UpgradeCardModal
                isOpen={isModalOpen}
                onLinkClick={handleChooseCurrentCardType}
                onButtonClick={handleUpgradeCard}
                calendarModalToggle={openCalendarModal}
            />
        </Wrapper>
    )
}

export default VoucherUnlimitedCard
